import { ChangeEvent, FocusEvent, forwardRef, KeyboardEvent, memo, MouseEvent, useCallback, useContext, useEffect, useState } from "react";
import moment from "moment-timezone";
import { convertFromServerTime, convertToServerTime, formatDateStringBase } from "../../../../utils";
import { AppConfigContext } from "../../../generic";
import { Input, InputProps } from "../Input";
import { TimeFieldMask, TimeFieldMaskProps, TimeFieldMaskRef } from "./TimeFieldMask";


export type TimeFieldProps = Omit<InputProps<TimeFieldMaskProps, TimeFieldMaskRef>, "component" | "value" | "onChange"> & {
  nullable?: boolean,
  onChange?: (date: string | null) => void,
  value?: string | null,
  timeFormat?: string,
  timeZone?: string,
  keepDateValue?: boolean,
  serverDateTimeFormat?: string,
}

export const TimeField = memo<TimeFieldProps>(forwardRef<TimeFieldMaskRef, TimeFieldProps>((
  {
    nullable = true,
    onChange,
    timeFormat,
    timeZone,
    onKeyDown,
    onClick,
    onBlur,
    value,
    keepDateValue = true,
    serverDateTimeFormat,
    isClearable,
    ...props
  }, ref) => {
  const appConfig = useContext(AppConfigContext);
  const usedServerDateTimeFormat = serverDateTimeFormat || appConfig.serverDateTimeFormatMoment;
  const usedTimeZone = timeZone || appConfig.timeZone;
  const usedTimeFormat = timeFormat || appConfig.timeFormatMoment;

  const [ stateValue, setValue ] = useState<string>();
  useEffect(() => {
    setValue(formatDateStringBase(value || null, usedTimeFormat, usedTimeZone, appConfig.serverTimeZone, appConfig.serverDateTimeFormatMoment, "__:__") || "__:__");
  }, [ value, usedTimeZone, usedTimeFormat, appConfig.serverTimeZone, appConfig.serverDateTimeFormatMoment ]);
  const convertToUtcString = useCallback((v: string) => {
    const tempDate = moment(v, usedTimeFormat);

    let date = moment.tz(usedTimeZone);
    if (keepDateValue && value) {
      const existingMoment = convertFromServerTime(value, appConfig.serverTimeZone, appConfig.serverDateTimeFormatMoment)
        .tz(usedTimeZone);
      if (existingMoment.isValid()) {
        date.year(existingMoment.year());
        date.month(existingMoment.month());
        date.date(existingMoment.date());
      }
    }

    date.hour(tempDate.hour());
    date.minute(tempDate.minute());
    date.second(tempDate.second());
    date.millisecond(tempDate.millisecond());

    return convertToServerTime(date, appConfig.serverTimeZone, usedServerDateTimeFormat);
  }, [ value, keepDateValue, usedServerDateTimeFormat, usedTimeFormat, usedTimeZone, appConfig.serverTimeZone, appConfig.serverDateTimeFormatMoment ]);
  const wrappedOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e?.target?.value;

    const isTime = /^(?:\d|[01]\d|2[0-3]):[0-5]\d$/;
    if (inputValue === "__:__") {
      if (onChange) {
        onChange(null);
      }
      setValue(undefined);
    } else {
      setValue(inputValue);
      if (isTime.test(inputValue) && inputValue !== value) {
        if (onChange) {
          onChange(convertToUtcString(inputValue));
        }
      }
    }
  }, [ convertToUtcString, onChange, setValue, value ]);
  const wrappedOnBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (nullable && inputValue.includes("_")) {
      if (onChange) {
        onChange(null);
      }
      setValue("__:__");
    } else {
      if (onChange) {
        onChange(convertToUtcString(inputValue.replace(/_/g, "0")));
      }
    }
    return onBlur?.(e);
  }, [ convertToUtcString, onBlur, onChange, setValue, nullable ]);
  const wrappedOnClick = useCallback((e: MouseEvent<HTMLInputElement>) => {
    if (stateValue === undefined) {
      (e.target as HTMLInputElement).setSelectionRange(0, 0);
    } else {
      const string = stateValue?.slice?.(0, (e.target as HTMLInputElement).selectionEnd!);
      const validation = string.includes("_");
      if (validation) {
        const value = stateValue?.split?.("");
        const index = value.findIndex((item: string) => item === "_");
        (e.target as HTMLInputElement).setSelectionRange(index, index);
      }
    }
    return onClick?.(e);
  }, [ onClick, stateValue ]);
  const wrappedOnKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowRight") {
      if (stateValue === undefined) {
        e.preventDefault();
      } else if (stateValue?.[(e.target as HTMLInputElement).selectionEnd!] === "_") {
        e.preventDefault();
      }
    }
    return onKeyDown?.(e);
  }, [ onKeyDown, stateValue ]);

  return (
    <Input
      {...props}
      ref={ref}
      value={stateValue}
      onChange={wrappedOnChange}
      onBlur={wrappedOnBlur}
      onClick={wrappedOnClick}
      onKeyDown={wrappedOnKeyDown}
      component={TimeFieldMask}
      isClearable={(stateValue !== "__:__") && isClearable}
    />
  );
}));


export default TimeField;
